/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { routeIterator } from '@app/common/utils/router';
import { AppState, TabRouteData } from '@app/core/types';
import { parseDashboardTemplateName } from '@app/monitoring-dashboard/domain/dashboard';
import {
  dashboardTemplates,
  removeDashboardTemplateOk,
  renameDashboardTemplateOk,
  saveDashboardTemplateOk,
} from '@app/monitoring-dashboard/ngrx/dashboard.actions';
import { routerNavigatedAction } from '@ngrx/router-store';
import { createReducer, on } from '@ngrx/store';
import { isString } from 'lodash-es';

export const dashboardTemplatesReducer = createReducer<AppState['dashboardTemplates']>(
  [],
  on(dashboardTemplates, (state, action) => action.dashboardTemplates),
  on(saveDashboardTemplateOk, (state, action) => [...state, action.dashboardTemplate]),
  on(renameDashboardTemplateOk, (state, action) =>
    state.map((template) =>
      template.id === action.dashboardTemplateId
        ? {
            ...template,
            name: action.name,
            workspace: {
              ...template.workspace,
              metadata: {
                ...template.workspace.metadata,
                title: parseDashboardTemplateName(action.name).value?.name ?? '',
              },
            },
          }
        : template,
    ),
  ),
  on(removeDashboardTemplateOk, (state, action) => state.filter(({ id }) => id !== action.dashboardTemplateId)),
);

export const lastOpenedDashboardReducer = createReducer<AppState['lastOpenedDashboard']>(
  {},
  on(routerNavigatedAction, (state, action) => {
    const routes = Array.from(routeIterator(action.payload.routerState.root));
    const tabRoute = routes.find((route) => (route.data as TabRouteData).isTab !== undefined);
    const parentRoute = routes.find((route) => route.firstChild === tabRoute);
    const groupId = parentRoute?.routeConfig?.path;
    const tabId = tabRoute?.url.at(0)?.path;
    if (isString(groupId) && isString(tabId)) {
      return { ...state, [groupId]: tabId };
    }
    return state;
  }),
);
